import { storage } from '../utils'
import { browserHistory } from 'react-router'
import { performFetch, submitItem, deleteItem } from './utils'
import * as api from '../constants/APIEndpoints'

/**
 * Load stored gear for a given person (uuid).
 *
 * @param {string} crew_uuid The UUID of the crewmember to retrieve the gear of.
 * @returns {Function} An action creator thunk.
 */
export function loadGear(crew_uuid) {
    return performFetch({
        type: 'LOAD_GEAR',
        urlData: { crew_uuid },
    })
}

/**
 * Send a PATCH request to a crewmember's gear. The idea here being that we only supply a subset of the full data in a
 * given request.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} crew_type The identifier of the crew type that the crewmember is updating the
 *                           gear for. This is mainly for 'virtual' fields that are keyed
                             secondarily by the crewtype, e.g. `additional_info`.
 * @param {boolean} shortcutPatch Whether to shortcut the patch and optimistically update.
 * @returns {Function} An action creator thunk.
 */
export function patchGear(data, crew_uuid, crew_type, shortcutPatch = true) {
    return performFetch({
        type: 'PATCH_GEAR',
        method: 'PATCH',
        data,
        urlData: { crew_uuid, crew_type },
        shortcutPatch,
    })
}

/**
 * Send a POST request to a crewmember's gear.
 *
 * This is currently used as a kind of PATCH, however - we POST a (complete) laptop/desktop computer from the
 * `ComputersSimple` component and that's what creates it in the API. So, in effect, we're PATCHing gear, but POSTing a
 * computer. It's all very confusing, but we don't think Django will allow us to inspect and creata  computer on a PATCH
 * request, so needs must.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @returns {Function} An action creator thunk.
 */
export function postGear(data, crew_uuid) {
    return performFetch({
        type: 'POST_GEAR',
        data,
        urlData: { crew_uuid },
    })
}

/**
 * Submit a given gear crew type (primary, secondary, tertiary) for validation and marking as completed.
 *
 * @param {string} type The type of crew type to commit ('primary', 'secondary', 'tertiary').
 * @param {string} crew_uuid The UUID of the crewmember to commit the gear for.
 * @param {object} data The data to send to the API.
 * @param {object} personalData The crew's personal data (for reference).
 * @returns {Function} An action creator thunk.
 */
export function commitGearData(type, crew_uuid, data, personalData) {
    const NEXT_URLS = {
        primary: '/gear/secondary',
        secondary: '/gear/tertiary',
        tertiary: '/profile',
    }

    if (! personalData.crew_secondary_type) {
        NEXT_URLS.primary = '/profile'
    }

    if (! personalData.crew_tertiary_type) {
        NEXT_URLS.secondary = '/profile'
    }

    return performFetch({
        type: 'COMMIT_GEAR',
        data,
        urlData: { crew_uuid, type },
        onSuccess: (data) => {
            // redirect
            browserHistory.push(NEXT_URLS[type])

            return { type: 'COMMIT_GEAR_SUCCESS', data }
        },
        onSuccessActions: [
            { type: 'ONBOARDING_STEP_COMPLETE', step: `gear_${ type }` },
        ],
    })
}

/**
 * Load list of video camera makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadVideoCameraMakes() {
    return performFetch({ type: 'LOAD_VIDEO_CAMERA_MAKES' })
}

/**
 * Load list of video camera models, optionally filtering to a given camera make (by uuid).
 *
 * @param {string} makeUUID The UUID of the video camera make to find models for.
 * @returns {Function} An action creator thunk.
 */
export function loadVideoCameraModels(makeUUID = null) {
    const urlData = {}

    if (makeUUID) {
        urlData['?'] = { make: makeUUID }
    }

    return performFetch({
        type: 'LOAD_VIDEO_CAMERA_MODELS',
        urlData,
    })
}

/**
 * Submit the add/edit video camera form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} camera_uuid The UUID of the video camera.
 * @returns {Function} An action creator thunk.
 */
export function submitVideoCamera(data, crew_uuid, camera_uuid) {
    return submitItem('VIDEO_CAMERA', data, crew_uuid, camera_uuid)
}

/**
 * Delete a video camera from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} camera_uuid The UUID of the video camera.
 * @returns {Function} An action creator thunk.
 */
export function deleteVideoCamera(crew_uuid, camera_uuid) {
    return deleteItem('VIDEO_CAMERA', crew_uuid, camera_uuid)
}

/**
 * Load list of still camera makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadStillCameraMakes() {
    return performFetch({ type: 'LOAD_STILL_CAMERA_MAKES' })
}

/**
 * Load list of still camera models, optionally filtering to a given camera make (by uuid).
 *
 * @param {string} makeUUID The UUID of the still camera make to find models for.
 * @returns {Function} An action creator thunk.
 */
export function loadStillCameraModels(makeUUID = null) {
    const urlData = {}

    if (makeUUID) {
        urlData['?'] = { make: makeUUID }
    }

    return performFetch({
        type: 'LOAD_STILL_CAMERA_MODELS',
        urlData,
    })
}

/**
 * Submit the add/edit still camera form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} camera_uuid The UUID of the still camera.
 * @returns {Function} An action creator thunk.
 */
export function submitStillCamera(data, crew_uuid, camera_uuid) {
    return submitItem('STILL_CAMERA', data, crew_uuid, camera_uuid)
}

/**
 * Delete a still camera from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} camera_uuid The UUID of the still camera.
 * @returns {Function} An action creator thunk.
 */
export function deleteStillCamera(crew_uuid, camera_uuid) {
    return deleteItem('STILL_CAMERA', crew_uuid, camera_uuid)
}

/**
 * Load list of camera makes.
 *
 * @param {string} type The type of camera that the makes should be retrieved for (video or still).
 * @returns {Function} An action creator thunk.
 */
export function loadCameraMakes(type) {
    if (type === 'video') {
        return loadVideoCameraMakes()
    }

    return loadStillCameraMakes()
}

/**
 * Load list of camera models.
 *
 * @param {string} type The type of camera that the models should be retrieved for (video or still).
 * @param {string} makeUUID The UUID of the camera make to find models for.
 * @returns {Function} An action creator thunk.
 */
export function loadCameraModels(type, makeUUID = null) {
    if (type === 'video') {
        return loadVideoCameraModels(makeUUID)
    }

    return loadStillCameraModels(makeUUID)
}

/**
 * Submit the add/edit camera form.
 *
 * @param {string} type The type of camera that is being submitted (video or still).
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} camera_uuid The UUID of the camera.
 * @returns {Function} An action creator thunk.
 */
export function submitCamera(type, data, crew_uuid, camera_uuid) {
    if (type === 'video') {
        return submitVideoCamera(data, crew_uuid, camera_uuid)
    }

    return submitStillCamera(data, crew_uuid, camera_uuid)
}

/**
 * Delete a camera from a crew member.
 *
 * @param {string} type The type of camera that is being deleted (video or still).
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} camera_uuid The UUID of the camera.
 * @returns {Function} An action creator thunk.
 */
export function deleteCamera(type, crew_uuid, camera_uuid) {
    if (type === 'video') {
        return deleteVideoCamera(crew_uuid, camera_uuid)
    }

    return deleteStillCamera(crew_uuid, camera_uuid)
}

/**
 * Load list of lens makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadLensMakes() {
    return performFetch({ type: 'LOAD_LENS_MAKES' })
}

/**
 * Load list of lens models, optionally filtering to a given lens make (by uuid).
 *
 * @param {string} makeUUID The UUID of the lens make to find models for.
 * @returns {Function} An action creator thunk.
 */
export function loadLensModels(makeUUID = null) {
    const urlData = {}

    if (makeUUID) {
        urlData['?'] = { make: makeUUID }
    }

    return performFetch({
        type: 'LOAD_LENS_MODELS',
        urlData,
    })
}

/**
 * Submit the add/edit lens form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} lens_uuid The UUID of the lens.
 * @returns {Function} An action creator thunk.
 */
export function submitLens(data, crew_uuid, lens_uuid) {
    return submitItem('LENS', data, crew_uuid, lens_uuid)
}

/**
 * Submit the add/edit lenses form.
 *
 * @param {Object} data The data to submit. Model should be an array of UUIDs of lenses to add.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @returns {Function} An action creator thunk.
 */
export function submitLenses(data, crew_uuid) {
    return submitItem('LENSES', data, crew_uuid)
}

/**
 * Delete a lens from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} lens_uuid The UUID of the lens.
 * @returns {Function} An action creator thunk.
 */
export function deleteLens(crew_uuid, lens_uuid) {
    return deleteItem('LENS', crew_uuid, lens_uuid)
}

/**
 * Load list of support equipment types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadSupportEquipmentTypes() {
    return performFetch({ type: 'LOAD_SUPPORT_EQUIPMENT_TYPES' })
}

/**
 * Submit the add/edit support equipment form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} support_equipment_uuid The UUID of the support equipment item.
 * @returns {Function} An action creator thunk.
 */
export function submitSupportEquipmentItem(data, crew_uuid, support_equipment_uuid) {
    return submitItem('SUPPORT_EQUIPMENT', data, crew_uuid, support_equipment_uuid)
}

/**
 * Delete an item of support equipment from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} support_equipment_uuid The UUID of the support equipment item.
 * @returns {Function} An action creator thunk.
 */
export function deleteSupportEquipmentItem(crew_uuid, support_equipment_uuid) {
    return deleteItem('SUPPORT_EQUIPMENT', crew_uuid, support_equipment_uuid)
}

/**
 * Load list of microphone types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadMicrophoneTypes() {
    return performFetch({ type: 'LOAD_MICROPHONE_TYPES' })
}

/**
 * Load list of microphone makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadMicrophoneMakes() {
    return performFetch({ type: 'LOAD_MICROPHONE_MAKES' })
}

/**
 * Submit the add/edit microphone form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} microphone_uuid The UUID of the microphone.
 * @returns {Function} An action creator thunk.
 */
export function submitMicrophone(data, crew_uuid, microphone_uuid) {
    return submitItem('MICROPHONE', data, crew_uuid, microphone_uuid)
}

/**
 * Delete a microphone from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} microphone_uuid The UUID of the microphone.
 * @returns {Function} An action creator thunk.
 */
export function deleteMicrophone(crew_uuid, microphone_uuid) {
    return deleteItem('MICROPHONE', crew_uuid, microphone_uuid)
}

/**
 * Load list of audio recorder makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadAudioRecorderMakes() {
    return performFetch({ type: 'LOAD_AUDIO_RECORDER_MAKES' })
}

/**
 * Submit the add/edit audio recorder form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} audio_recorder_uuid The UUID of the audio recorder.
 * @returns {Function} An action creator thunk.
 */
export function submitAudioRecorder(data, crew_uuid, audio_recorder_uuid) {
    return submitItem('AUDIO_RECORDER', data, crew_uuid, audio_recorder_uuid)
}

/**
 * Delete an audio recorder from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} audio_recorder_uuid The UUID of the audio recorder.
 * @returns {Function} An action creator thunk.
 */
export function deleteAudioRecorder(crew_uuid, audio_recorder_uuid) {
    return deleteItem('AUDIO_RECORDER', crew_uuid, audio_recorder_uuid)
}

/**
 * Load list of video recorder makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadVideoRecorderMakes() {
    return performFetch({ type: 'LOAD_VIDEO_RECORDER_MAKES' })
}

/**
 * Submit the add/edit video recorder form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} video_recorder_uuid The UUID of the video recorder.
 * @returns {Function} An action creator thunk.
 */
export function submitVideoRecorder(data, crew_uuid, video_recorder_uuid) {
    return submitItem('VIDEO_RECORDER', data, crew_uuid, video_recorder_uuid)
}

/**
 * Delete a video recorder from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} video_recorder_uuid The UUID of the video recorder.
 * @returns {Function} An action creator thunk.
 */
export function deleteVideoRecorder(crew_uuid, video_recorder_uuid) {
    return deleteItem('VIDEO_RECORDER', crew_uuid, video_recorder_uuid)
}

/**
 * Load list of wireless video system types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadWirelessVideoSystemTypes() {
    return performFetch({ type: 'LOAD_WIRELESS_VIDEO_SYSTEM_TYPES' })
}

/**
 * Load list of wireless video system makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadWirelessVideoSystemMakes() {
    return performFetch({ type: 'LOAD_WIRELESS_VIDEO_SYSTEM_MAKES' })
}

/**
 * Submit the add/edit wireless video system form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} wireless_system_uuid The UUID of the wireless video system.
 * @returns {Function} An action creator thunk.
 */
export function submitWirelessVideoSystem(data, crew_uuid, wireless_system_uuid) {
    return submitItem('WIRELESS_VIDEO_SYSTEM', data, crew_uuid, wireless_system_uuid)
}

/**
 * Delete a wireless video system from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} wireless_system_uuid The UUID of the wireless video system.
 * @returns {Function} An action creator thunk.
 */
export function deleteWirelessVideoSystem(crew_uuid, wireless_system_uuid) {
    return deleteItem('WIRELESS_VIDEO_SYSTEM', crew_uuid, wireless_system_uuid)
}

/**
 * Load list of wireless audio system types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadWirelessAudioSystemTypes() {
    return performFetch({ type: 'LOAD_WIRELESS_AUDIO_SYSTEM_TYPES' })
}

/**
 * Load list of wireless audio system makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadWirelessAudioSystemMakes() {
    return performFetch({ type: 'LOAD_WIRELESS_AUDIO_SYSTEM_MAKES' })
}

/**
 * Submit the add/edit wireless audio system form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} wireless_system_uuid The UUID of the wireless audio system.
 * @returns {Function} An action creator thunk.
 */
export function submitWirelessAudioSystem(data, crew_uuid, wireless_system_uuid) {
    return submitItem('WIRELESS_AUDIO_SYSTEM', data, crew_uuid, wireless_system_uuid)
}

/**
 * Delete a wireless audio system from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} wireless_system_uuid The UUID of the wireless audio system.
 * @returns {Function} An action creator thunk.
 */
export function deleteWirelessAudioSystem(crew_uuid, wireless_system_uuid) {
    return deleteItem('WIRELESS_AUDIO_SYSTEM', crew_uuid, wireless_system_uuid)
}

/**
 * Load list of wireless video system types.
 *
 * @param {string} type The type of wireless system that the types should be retrieved for (video or audio).
 * @returns {Function} An action creator thunk.
 */
export function loadWirelessSystemTypes(type) {
    if (type === 'video') {
        return loadWirelessVideoSystemTypes()
    }

    return loadWirelessAudioSystemTypes()
}

/**
 * Load list of wireless video system makes.
 *
 * @param {string} type The type of wireless system that the makes should be retrieved for (video or audio).
 * @returns {Function} An action creator thunk.
 */
export function loadWirelessSystemMakes(type) {
    if (type === 'video') {
        return loadWirelessVideoSystemMakes()
    }

    return loadWirelessAudioSystemMakes()
}

/**
 * Submit the add/edit wireless system form.
 *
 * @param {string} type The type of wireless system that is being submitted (video or still).
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} wireless_system_uuid The UUID of the wireless system.
 * @returns {Function} An action creator thunk.
 */
export function submitWirelessSystem(type, data, crew_uuid, wireless_system_uuid) {
    if (type === 'video') {
        return submitWirelessVideoSystem(data, crew_uuid, wireless_system_uuid)
    }

    return submitWirelessAudioSystem(data, crew_uuid, wireless_system_uuid)
}

/**
 * Delete a wireless system from a crew member.
 *
 * @param {string} type The type of wireless system that is being deleted (video or still).
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} wireless_system_uuid The UUID of the wireless system.
 * @returns {Function} An action creator thunk.
 */
export function deleteWirelessSystem(type, crew_uuid, wireless_system_uuid) {
    if (type === 'video') {
        return deleteWirelessVideoSystem(crew_uuid, wireless_system_uuid)
    }

    return deleteWirelessAudioSystem(crew_uuid, wireless_system_uuid)
}

/**
 * Load list of video lighting kit types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadVideoLightingKitTypes() {
    return performFetch({ type: 'LOAD_VIDEO_LIGHTING_KIT_TYPES' })
}

/**
 * Load list of video lighting kit makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadVideoLightingKitMakes() {
    return performFetch({ type: 'LOAD_VIDEO_LIGHTING_KIT_MAKES' })
}

/**
 * Submit the add/edit video lighting kit form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} lighting_kit_uuid The UUID of the video lighting kit.
 * @returns {Function} An action creator thunk.
 */
export function submitVideoLightingKit(data, crew_uuid, lighting_kit_uuid) {
    return submitItem('VIDEO_LIGHTING_KIT', data, crew_uuid, lighting_kit_uuid)
}

/**
 * Delete a video lighting kit from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} lighting_kit_uuid The UUID of the video lighting kit.
 * @returns {Function} An action creator thunk.
 */
export function deleteVideoLightingKit(crew_uuid, lighting_kit_uuid) {
    return deleteItem('VIDEO_LIGHTING_KIT', crew_uuid, lighting_kit_uuid)
}

/**
 * Load list of still lighting kit types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadStillLightingKitTypes() {
    return performFetch({ type: 'LOAD_STILL_LIGHTING_KIT_TYPES' })
}

/**
 * Load list of still lighting kit makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadStillLightingKitMakes() {
    return performFetch({ type: 'LOAD_STILL_LIGHTING_KIT_MAKES' })
}

/**
 * Submit the add/edit still lighting kit form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} lighting_kit_uuid The UUID of the still lighting kit.
 * @returns {Function} An action creator thunk.
 */
export function submitStillLightingKit(data, crew_uuid, lighting_kit_uuid) {
    return submitItem('STILL_LIGHTING_KIT', data, crew_uuid, lighting_kit_uuid)
}

/**
 * Delete a still lighting kit from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} lighting_kit_uuid The UUID of the still lighting kit.
 * @returns {Function} An action creator thunk.
 */
export function deleteStillLightingKit(crew_uuid, lighting_kit_uuid) {
    return deleteItem('STILL_LIGHTING_KIT', crew_uuid, lighting_kit_uuid)
}

/**
 * Load list of lighting kit types.
 *
 * @param {string} type The type of lighting kit that the types should be retrieved for (video or still).
 * @returns {Function} An action creator thunk.
 */
export function loadLightingKitTypes(type) {
    if (type === 'video') {
        return loadVideoLightingKitTypes()
    }

    return loadStillLightingKitTypes()
}

/**
 * Load list of lighting kit makes.
 *
 * @param {string} type The type of lighting kit that the makes should be retrieved for (video or still).
 * @returns {Function} An action creator thunk.
 */
export function loadLightingKitMakes(type) {
    if (type === 'video') {
        return loadVideoLightingKitMakes()
    }

    return loadStillLightingKitMakes()
}

/**
 * Submit the add/edit lighting kit form.
 *
 * @param {string} type The type of lighting kit that is being submitted (video or still).
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} lighting_kit_uuid The UUID of the lighting kit.
 * @returns {Function} An action creator thunk.
 */
export function submitLightingKit(type, data, crew_uuid, lighting_kit_uuid) {
    if (type === 'video') {
        return submitVideoLightingKit(data, crew_uuid, lighting_kit_uuid)
    }

    return submitStillLightingKit(data, crew_uuid, lighting_kit_uuid)
}

/**
 * Delete a lighting kit from a crew member.
 *
 * @param {string} type The type of lighting kit that is being deleted (video or still).
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} lighting_kit_uuid The UUID of the lighting kit.
 * @returns {Function} An action creator thunk.
 */
export function deleteLightingKit(type, crew_uuid, lighting_kit_uuid) {
    if (type === 'video') {
        return deleteVideoLightingKit(crew_uuid, lighting_kit_uuid)
    }

    return deleteStillLightingKit(crew_uuid, lighting_kit_uuid)
}

/**
 * Load list of monitor types.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadMonitorTypes() {
    return performFetch({ type: 'LOAD_MONITOR_TYPES' })
}

/**
 * Load list of monitor makes.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadMonitorMakes() {
    return performFetch({ type: 'LOAD_MONITOR_MAKES' })
}

/**
 * Submit the add/edit monitor form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} monitor_uuid The UUID of the monitor.
 * @returns {Function} An action creator thunk.
 */
export function submitMonitor(data, crew_uuid, monitor_uuid) {
    return submitItem('MONITOR', data, crew_uuid, monitor_uuid)
}

/**
 * Delete a monitor from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} monitor_uuid The UUID of the monitor.
 * @returns {Function} An action creator thunk.
 */
export function deleteMonitor(crew_uuid, monitor_uuid) {
    return deleteItem('MONITOR', crew_uuid, monitor_uuid)
}

/**
 * Submit the add/edit computer form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} computer_uuid The UUID of the computer.
 * @returns {Function} An action creator thunk.
 */
export function submitComputer(data, crew_uuid, computer_uuid) {
    return submitItem('COMPUTER', data, crew_uuid, computer_uuid)
}

/**
 * Delete a computer from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} computer_uuid The UUID of the computer.
 * @returns {Function} An action creator thunk.
 */
export function deleteComputer(crew_uuid, computer_uuid) {
    return deleteItem('COMPUTER', crew_uuid, computer_uuid)
}

/**
 * Submit the add/edit additional gear form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} additional_gear_uuid The UUID of the additional gear item.
 * @returns {Function} An action creator thunk.
 */
export function submitAdditionalGear(data, crew_uuid, additional_gear_uuid) {
    return submitItem('ADDITIONAL_GEAR', data, crew_uuid, additional_gear_uuid)
}

/**
 * Delete a additional gear item from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} additional_gear_uuid The UUID of the additional gear item.
 * @returns {Function} An action creator thunk.
 */
export function deleteAdditionalGear(crew_uuid, additional_gear_uuid) {
    return deleteItem('ADDITIONAL_GEAR', crew_uuid, additional_gear_uuid)
}

/**
 * Submit the add/edit droneform.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} drone_uuid The UUID of the droneitem.
 * @returns {Function} An action creator thunk.
 */
export function submitDrone(data, crew_uuid, drone_uuid) {
    return submitItem('DRONE', data, crew_uuid, drone_uuid)
}

/**
 * Delete a drone from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} drone_uuid The UUID of the droneitem.
 * @returns {Function} An action creator thunk.
 */
export function deleteDrone(crew_uuid, drone_uuid) {
    return deleteItem('DRONE', crew_uuid, drone_uuid)
}

/**
 * Submit the add/edit DIT software package form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} dit_software_uuid The UUID of the DIT software package item.
 * @returns {Function} An action creator thunk.
 */
export function submitDITSoftware(data, crew_uuid, dit_software_uuid) {
    return submitItem('DIT_SOFTWARE', data, crew_uuid, dit_software_uuid)
}

/**
 * Delete a DIT software package item from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} dit_software_uuid The UUID of the DIT software package item.
 * @returns {Function} An action creator thunk.
 */
export function deleteDITSoftware(crew_uuid, dit_software_uuid) {
    return deleteItem('DIT_SOFTWARE', crew_uuid, dit_software_uuid)
}

/**
 * Load list of transportation options.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadTransportationOptions() {
    return performFetch({ type: 'LOAD_TRANSPORTATION_OPTIONS' })
}

/**
 * Load list of software options.
 *
 * @returns {Function} An action creator thunk.
 */
export function loadSoftwareOptions() {
    return performFetch({ type: 'LOAD_SOFTWARE_OPTIONS' })
}

/**
 * Submit the add/edit additional software form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} additional_software_uuid The UUID of the additional software item.
 * @returns {Function} An action creator thunk.
 */
export function submitAdditionalSoftware(data, crew_uuid, additional_software_uuid) {
    return submitItem('ADDITIONAL_SOFTWARE', data, crew_uuid, additional_software_uuid)
}

/**
 * Delete a additional software item from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} additional_software_uuid The UUID of the additional software item.
 * @returns {Function} An action creator thunk.
 */
export function deleteAdditionalSoftware(crew_uuid, additional_software_uuid) {
    return deleteItem('ADDITIONAL_SOFTWARE', crew_uuid, additional_software_uuid)
}

/**
 * Submit the add/edit additional hardware form.
 *
 * @param {Object} data The data to submit.
 * @param {string} crew_uuid The UUID of the crewmember to update the gear for.
 * @param {string} additional_hardware_uuid The UUID of the additional hardware item.
 * @returns {Function} An action creator thunk.
 */
export function submitAdditionalHardware(data, crew_uuid, additional_hardware_uuid) {
    return submitItem('ADDITIONAL_HARDWARE', data, crew_uuid, additional_hardware_uuid)
}

/**
 * Delete a additional hardware item from a crew member.
 *
 * @param {string} crew_uuid The UUID of the crewmember to delete the gear for.
 * @param {string} additional_hardware_uuid The UUID of the additional hardware item.
 * @returns {Function} An action creator thunk.
 */
export function deleteAdditionalHardware(crew_uuid, additional_hardware_uuid) {
    return deleteItem('ADDITIONAL_HARDWARE', crew_uuid, additional_hardware_uuid)
}

export function uploadEquipmentList(file, uuid) {
    // Create a form data object to handle the file
    const formData = new FormData()

    formData.append('equipment_list', file)

    return (dispatch) => {
        dispatch({ type: 'LOADING' })

        const url = `${ api.UPLOAD_EQUIPMENT_LIST_URL }${ uuid }/`
        const xhr = new XMLHttpRequest()

        xhr.onload = () => {
            dispatch({ type: 'LOADING_COMPLETE' })

            // eslint-disable-next-line no-magic-numbers
            if (xhr.status === 200) {
                dispatch({ type: 'UPLOAD_EQUIPMENT_LIST_SUCCESS', data: JSON.parse(xhr.responseText) })
            } else {
                dispatch({ type: 'UPLOAD_EQUIPMENT_LIST_ERROR', errors: JSON.parse(xhr.responseText) })
            }
        }

        xhr.onerror = () => {
            console.log('Equipment list upload error.')
        }

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                // eslint-disable-next-line no-magic-numbers
                const percent = Math.round((event.loaded / event.total) * 100)

                dispatch({ type: 'UPLOAD_EQUIPMENT_LIST_PROGRESS', percent })
            }
        }

        xhr.open('POST', url)
        xhr.setRequestHeader('Authorization', `Token ${ storage.getItem('authToken') }`)
        xhr.send(formData)
    }
}

export function uploadEquipmentListError(errors) {
    return { type: 'UPLOAD_EQUIPMENT_LIST_ERROR', errors }
}



// WEBPACK FOOTER //
// ./app/actions/gear.js